import { ref, onMounted, onBeforeUnmount } from 'vue';

/**
 * Composable for managing worksheet selection with auto-refresh
 * Shared across components that need to select worksheets
 */
export function useWorksheetSelection() {
    const worksheets = ref([]);
    let worksheetAddedHandler = null;
    let worksheetDeletedHandler = null;
    let worksheetNameChangedHandler = null;

    // Function to fetch worksheet names
    const fetchWorksheets = async () => {
        try {
            await window.Excel.run(async (context) => {
                const sheets = context.workbook.worksheets;
                sheets.load('items/name');
                await context.sync();

                worksheets.value = sheets.items.map(sheet => sheet.name);
            });
        } catch (error) {
            console.error('Error fetching worksheets:', error);
        }
    };

    // Setup event handlers for worksheet changes
    const setupWorksheetEventHandlers = async () => {
        try {
            await window.Excel.run(async (context) => {
                const worksheetCollection = context.workbook.worksheets;

                // Register event handler for worksheet added
                worksheetAddedHandler = worksheetCollection.onAdded.add(async () => {
                    await fetchWorksheets();
                });

                // Register event handler for worksheet deleted
                worksheetDeletedHandler = worksheetCollection.onDeleted.add(async () => {
                    await fetchWorksheets();
                });

                // Register event handler for worksheet name changed
                worksheetNameChangedHandler = worksheetCollection.onNameChanged.add(async () => {
                    await fetchWorksheets();
                });

                await context.sync();
            });
        } catch (error) {
            console.error('Error setting up worksheet event handlers:', error);
        }
    };

    // Remove event handlers
    const removeWorksheetEventHandlers = async () => {
        try {
            await window.Excel.run(async (context) => {
                if (worksheetAddedHandler) {
                    worksheetAddedHandler.remove();
                }
                if (worksheetDeletedHandler) {
                    worksheetDeletedHandler.remove();
                }
                if (worksheetNameChangedHandler) {
                    worksheetNameChangedHandler.remove();
                }
                await context.sync();
            });
        } catch (error) {
            console.error('Error removing worksheet event handlers:', error);
        }
    };

    // Initialize on mount
    const initialize = async () => {
        await fetchWorksheets();
        await setupWorksheetEventHandlers();
    };

    // Cleanup on unmount
    const cleanup = async () => {
        await removeWorksheetEventHandlers();
    };

    // Return for use in setup
    onMounted(initialize);
    onBeforeUnmount(cleanup);

    return {
        worksheets,
        fetchWorksheets
    };
}
